import React, { useEffect, useState } from 'react'
import {
  Button,
  StatusPill,
  ConfirmActionModal,
  Textarea,
  AppDialog,
} from '../../Common'
import { useNls } from '../../../nls/nlsProvider'
import type {
  OutsideApproval,
  OutsideApprovalSubjectType,
} from '../../../types/ops'
import {
  subscribeApprovalsForSubject,
  reissueApproverLink,
  revokeOutsideApproval,
} from '../../../services/opsApprovalsService'
import './approvals.css'

export interface OutsideApprovalsSectionProps {
  workspaceId: string
  subjectType: OutsideApprovalSubjectType
  subjectId: string
  subjectVersion?: number
  canSend?: boolean
  onOpenSendDialog?: () => void
  userRole?: string | null
  isAdmin?: boolean
  currentUid?: string | null
}

export const OutsideApprovalsSection: React.FC<OutsideApprovalsSectionProps> = ({
  workspaceId,
  subjectType,
  subjectId,
  subjectVersion,
  canSend,
  onOpenSendDialog,
  userRole,
  isAdmin,
  currentUid,
}) => {
  const { t } = useNls()

  const [approvals, setApprovals] = useState<OutsideApproval[]>([])
  const [busy, setBusy] = useState<boolean>(false)
  const [actionError, setActionError] = useState<string | null>(null)

  // Reissue modal state
  const [reissuedLink, setReissuedLink] = useState<{
    phoneMasked: string
    token: string
  } | null>(null)
  const [copiedToken, setCopiedToken] = useState<string | null>(null)

  // Revoke modal state
  const [revokeApprovalTarget, setRevokeApprovalTarget] = useState<OutsideApproval | null>(null)
  const [revokeReason, setRevokeReason] = useState<string>('')

  useEffect(() => {
    if (!workspaceId || !subjectId) return
    const unsub = subscribeApprovalsForSubject(
      workspaceId,
      subjectType,
      subjectId,
      (list) => {
        setApprovals(list)
      },
    )
    return () => unsub()
  }, [workspaceId, subjectType, subjectId])

  const canManageApproval = (appr: OutsideApproval): boolean => {
    const role = (userRole || '').toUpperCase()
    if (isAdmin || role === 'OWNER' || role === 'ADMIN') return true
    return Boolean(currentUid && appr.createdBy === currentUid)
  }

  const handleReissue = async (approvalId: string, approverId: string, phoneMasked: string) => {
    setActionError(null)
    setBusy(true)
    try {
      const opId = crypto.randomUUID()
      const res = await reissueApproverLink({
        workspaceId,
        approvalId,
        approverId,
        opId,
      })
      setReissuedLink({ phoneMasked, token: res.token })
    } catch (err: unknown) {
      setActionError((err as Error)?.message || String(err))
    } finally {
      setBusy(false)
    }
  }

  const handleConfirmRevoke = async () => {
    if (!revokeApprovalTarget) return
    const reason = revokeReason.trim()
    if (reason.length < 3 || reason.length > 500) return

    setActionError(null)
    setBusy(true)
    try {
      const opId = crypto.randomUUID()
      await revokeOutsideApproval({
        workspaceId,
        approvalId: revokeApprovalTarget.id,
        reason,
        opId,
      })
      setRevokeApprovalTarget(null)
      setRevokeReason('')
    } catch (err: unknown) {
      setActionError((err as Error)?.message || String(err))
    } finally {
      setBusy(false)
    }
  }

  const handleCopyReissued = async (token: string) => {
    const url = `${window.location.origin}/approve/${token}`
    try {
      await navigator.clipboard.writeText(url)
      setCopiedToken(token)
      setTimeout(() => setCopiedToken(null), 2500)
    } catch {
      // Fallback
    }
  }

  const handleShareReissued = async (token: string, phoneMasked: string) => {
    const url = `${window.location.origin}/approve/${token}`
    if (typeof navigator !== 'undefined' && navigator.share) {
      try {
        await navigator.share({
          title: t('NLSOpsOutsideApprovalsTitle'),
          text: phoneMasked ? `${phoneMasked}: approval link` : url,
          url,
        })
      } catch {
        // Fallback
      }
    } else {
      void handleCopyReissued(token)
    }
  }

  return (
    <section className="outsideApprovalsSection" aria-label={t('NLSOpsOutsideApprovalsTitle')}>
      <div className="outsideApprovalsSectionHeader">
        <h3 className="outsideApprovalsSectionTitle">{t('NLSOpsOutsideApprovalsTitle')}</h3>
        {canSend && onOpenSendDialog && (
          <Button variant="secondary" size="sm" onClick={onOpenSendDialog}>
            + {t('NLSOpsOutsideApprovalsTitle')}
          </Button>
        )}
      </div>

      {actionError && (
        <div className="outsideApprovalErrorBanner" role="alert">
          {actionError}
        </div>
      )}

      {approvals.length === 0 ? (
        <p className="outsideApprovalsEmpty">
          {t('NLSOpsNoCorrectionsYet')}
        </p>
      ) : (
        <div className="outsideApprovalsList">
          {approvals.map((appr) => {
            const isVersionMismatched =
              subjectVersion !== undefined && appr.subjectVersion !== subjectVersion
            const isAllowedToManage = canManageApproval(appr)

            const sortedEvents = [
              ...(((appr as unknown as { events?: Array<{ at: number; type: string; byName?: string; phoneMasked?: string; comment?: string; reason?: string }> }).events || [])),
            ].sort((a, b) => b.at - a.at)

            return (
              <div key={appr.id} className="outsideApprovalItemCard">
                <div className="outsideApprovalCardTop">
                  <div className="outsideApprovalCardMeta">
                    <StatusPill
                      tone={
                        appr.status === 'APPROVED'
                          ? 'success'
                          : appr.status === 'REJECTED'
                            ? 'danger'
                            : appr.status === 'SENT'
                              ? 'warning'
                              : 'neutral'
                      }
                    >
                      {appr.status}
                    </StatusPill>
                    <span>
                      Mode: <strong>{appr.mode}</strong>
                    </span>
                    <span>
                      {t('NLSOpsApprovalExpiresOn')}:{' '}
                      {new Date(appr.expiresAt).toLocaleDateString()}
                    </span>
                  </div>

                  {appr.status === 'SENT' && isAllowedToManage && (
                    <Button
                      variant="danger"
                      size="sm"
                      onClick={() => setRevokeApprovalTarget(appr)}
                      disabled={busy}
                    >
                      {t('NLSOpsApprovalRevoke')}
                    </Button>
                  )}
                </div>

                {isVersionMismatched && (
                  <div className="outsideApprovalWarningBanner" role="alert">
                    {t('NLSOpsApprovalItemChangedWarning')}
                  </div>
                )}

                {appr.message && (
                  <p style={{ margin: '0 0 12px', fontSize: '0.875rem', fontStyle: 'italic' }}>
                    "{appr.message}"
                  </p>
                )}

                <table className="outsideApprovalApproversTable" aria-label="Approvers table">
                  <thead>
                    <tr>
                      <th>Approver</th>
                      <th>Phone</th>
                      <th>Status</th>
                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {appr.approvers.map((item) => {
                      const canReissueItem =
                        appr.status === 'SENT' &&
                        (item.status === 'PENDING' || item.status === 'VIEWED') &&
                        isAllowedToManage

                      return (
                        <tr key={item.approverId}>
                          <td style={{ fontWeight: 500 }}>{item.name}</td>
                          <td>{item.phoneMasked}</td>
                          <td>
                            <StatusPill
                              tone={
                                item.status === 'APPROVED'
                                  ? 'success'
                                  : item.status === 'REJECTED'
                                    ? 'danger'
                                    : item.status === 'VIEWED'
                                      ? 'info'
                                      : 'neutral'
                              }
                            >
                              {item.status}
                            </StatusPill>
                            {item.comment && (
                              <div className="outsideApproverComment">
                                "{item.comment}"
                              </div>
                            )}
                          </td>
                          <td>
                            {canReissueItem && (
                              <Button
                                variant="tertiary"
                                size="sm"
                                onClick={() =>
                                  handleReissue(appr.id, item.approverId, item.phoneMasked)
                                }
                                disabled={busy}
                              >
                                {t('NLSOpsApprovalReissueLink')}
                              </Button>
                            )}
                          </td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>

                {sortedEvents.length > 0 && (
                  <div className="outsideApprovalEventsList">
                    <span style={{ fontWeight: 600, display: 'block', marginBottom: 4 }}>
                      History
                    </span>
                    {sortedEvents.map((ev, i) => (
                      <div key={i} className="outsideApprovalEventRow">
                        <span className="outsideApprovalEventTime">
                          {new Date(ev.at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                        </span>
                        <span>
                          <strong>{ev.type}</strong>
                          {ev.byName ? ` by ${ev.byName}` : ev.phoneMasked ? ` (${ev.phoneMasked})` : ''}
                          {ev.comment ? `: "${ev.comment}"` : ''}
                          {ev.reason ? `: "${ev.reason}"` : ''}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )
          })}
        </div>
      )}

      {/* Revoke Confirmation Modal */}
      {revokeApprovalTarget && (
        <ConfirmActionModal
          open={Boolean(revokeApprovalTarget)}
          onClose={() => {
            setRevokeApprovalTarget(null)
            setRevokeReason('')
          }}
          onConfirm={handleConfirmRevoke}
          title={t('NLSOpsApprovalRevokeConfirm')}
          body={t('NLSOpsApprovalRevokeReason')}
          confirmLabel={t('NLSOpsApprovalRevoke')}
          danger
          busy={busy}
          confirmDisabled={revokeReason.trim().length < 3 || revokeReason.trim().length > 500}
        >
          <div style={{ marginTop: 12 }}>
            <label style={{ display: 'block', fontWeight: 600, fontSize: '0.875rem', marginBottom: 4 }}>
              {t('NLSOpsApprovalRevokeReason')}
            </label>
            <Textarea
              value={revokeReason}
              onChange={(val) => {
                const str = typeof val === 'string' ? val : (val as { target?: { value?: string } })?.target?.value ?? String(val ?? '')
                setRevokeReason(str)
              }}
              placeholder={t('NLSOpsApprovalRevokeReason')}
              maxLength={500}
              disabled={busy}
              rows={3}
            />
          </div>
        </ConfirmActionModal>
      )}

      {/* Reissued Link Modal */}
      {reissuedLink && (
        <AppDialog
          open={Boolean(reissuedLink)}
          onDismiss={() => setReissuedLink(null)}
          title={t('NLSOpsApprovalLinksShownOnce')}
          size="md"
          actions={[
            {
              label: t('NLSOpsRequestsVerifiedLabel') || 'Done',
              variant: 'primary',
              onClick: () => setReissuedLink(null),
            },
          ]}
        >
          <div>
            <p className="outsideApprovalLinksNotice">
              {t('NLSOpsApprovalLinksShownOnce')}
            </p>
            <div className="outsideApprovalLinkRow">
              <div className="outsideApprovalLinkHeader">
                <span style={{ fontWeight: 600 }}>{reissuedLink.phoneMasked}</span>
              </div>
              <input
                type="text"
                readOnly
                value={`${typeof window !== 'undefined' ? window.location.origin : ''}/approve/${reissuedLink.token}`}
                className="outsideApprovalLinkUrl"
                aria-label={reissuedLink.phoneMasked}
              />
              <div style={{ display: 'flex', gap: 8 }}>
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => handleCopyReissued(reissuedLink.token)}
                >
                  {copiedToken === reissuedLink.token
                    ? t('NLSOpsApprovalLinkCopied')
                    : t('NLSOpsApprovalCopyLink')}
                </Button>
                {typeof navigator !== 'undefined' && 'share' in navigator ? (
                  <Button
                    variant="tertiary"
                    size="sm"
                    onClick={() => handleShareReissued(reissuedLink.token, reissuedLink.phoneMasked)}
                  >
                    {t('NLSOpsApprovalShareLink')}
                  </Button>
                ) : null}
              </div>
            </div>
          </div>
        </AppDialog>
      )}
    </section>
  )
}
